import { formatDate, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReportService } from '@core/report/report.service';
import { select, Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingButtonDirective } from '@platform/components/buttons/loading-button/loading-button.directive';
import { selectPortalConfig } from '@platform/platform.selectors';
import { PortalConfig } from '@platform/platform.state';
import { pendingFlag, withPendingFlag } from '@platform/shared/utilities/pending-flag';
import { ToastService } from '@platform/toast/toast.service';
import { RootState } from '@root/app.state';
import saveAs from 'file-saver';
import moment from 'moment';
import { AdminActionItemComponent } from '../ui/admin-action-item.component';
import { AdminPageSectionComponent } from '../ui/admin-page-section.component';
import { ButtonIndicatorDownloadComponent } from '../ui/button-indicator-download.component';

@Component({
  selector: 'app-charging-report-section',
  imports: [
    NgIf,
    AdminPageSectionComponent,
    AdminActionItemComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatDatepickerModule,
    LoadingButtonDirective,
    ButtonIndicatorDownloadComponent,
  ],
  templateUrl: './charging-report-section.component.html',
  styleUrl: './charging-report-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChargingReportSectionComponent {
  public readonly chargingReportPending = pendingFlag();
  public readonly fileProcessReportPending = pendingFlag();
  public readonly portalConfig: Signal<PortalConfig | null | undefined>;

  public readonly chargingReportForm = this.formBuilder.group({
    periodStartDate: [moment().subtract(1, 'months').startOf('month'), [Validators.required]],
    periodEndDate: [moment().subtract(1, 'months').endOf('month'), [Validators.required]],
  });

  constructor(
    private store$: Store<RootState>,
    private reportService: ReportService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
  ) {
    this.portalConfig = toSignal(store$.pipe(select(selectPortalConfig)));
  }

  public downloadChargingReport() {
    if (!this.chargingReportForm.valid) {
      return;
    }

    this.reportService
      .getChargingReport(this.chargingReportForm.value.periodStartDate!, this.chargingReportForm.value.periodEndDate!)
      .pipe(withPendingFlag(this.chargingReportPending))
      .subscribe({
        next: (response) => saveAs(response, `Charging_Report_MYO2_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  public downloadFileProcessReport() {
    this.reportService
      .getFileProcessReport()
      .pipe(withPendingFlag(this.fileProcessReportPending))
      .subscribe({
        next: (response) => saveAs(response, `File_Process_Report_MYO2_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }
}
